/**
 * ConversationList - Lista de conversas com infinite scroll
 * Usa IntersectionObserver para carregar mais itens ao rolar
 */

import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useInfiniteConversations } from '../hooks';
import { ConversationItem } from './ConversationItem';
import type { ChatFiltersState } from './ChatFilterDefinitions';
import { ListPageEmpty } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
interface ConversationListProps {
  /** Tipo de conversas a exibir */
  conversationType: 'individual' | 'group' | 'ai';
  /** ID da conversa selecionada */
  selectedConversationId?: string;
  /** Callback ao selecionar conversa - passa ID e opcionalmente o termo de busca */
  onSelectConversation?: (conversationId: string, searchTerm?: string) => void;
  /** Se a sidebar está expandida */
  expanded?: boolean;
  /** Filtros aplicados */
  filters?: ChatFiltersState;
  /** Query de busca */
  searchQuery?: string;
}

/**
 * Lista de conversas com infinite scroll automático
 *
 * @example
 * \`\`\`tsx
 * <ConversationList
 *   conversationType="individual"
 *   selectedConversationId={currentId}
 *   onSelectConversation={(id) => navigate(\`/chat/\${id}\`)}
 *   filters={filters}
 *   searchQuery={searchQuery}
 * />
 * \`\`\`
 */
export const ConversationList: React.FC<ConversationListProps> = ({
  conversationType,
  selectedConversationId,
  onSelectConversation,
  expanded = true,
  filters = {},
  searchQuery = ''
}) => {
  const { state } = useAuth();

  // ✅ Mapear filtros do componente para formato do backend
  const additionalFilters = useMemo(() => {
    const backendFilters: any = {};

    // Multiselect array filters
    if (filters.status && filters.status.length > 0) {
      backendFilters.status = filters.status;
    }
    if (filters.priority && filters.priority.length > 0) {
      backendFilters.priority = filters.priority[0]; // Backend expects single value
    }
    if (filters.channelType && filters.channelType.length > 0) {
      backendFilters.channelType = filters.channelType[0]; // Backend resolves to channelIds
    }
    if (filters.channelId && filters.channelId.length > 0) {
      backendFilters.channelId = filters.channelId;
    }
    if (filters.teamId && filters.teamId.length > 0) {
      backendFilters.teamId = filters.teamId[0]; // Backend expects single value
    }
    if (filters.assigneeId && filters.assigneeId.length > 0) {
      backendFilters.assigneeId = filters.assigneeId;
    }
    if (filters.tags && filters.tags.length > 0) {
      backendFilters.tags = filters.tags;
    }
    // ✅ Filtro "Etapa no funil" (resolvido no backend via leads → contactIds)
    if (filters.stepId && filters.stepId.length > 0) {
      backendFilters.stepId = filters.stepId;
    }
    // ✅ Filtro "Cliente" (resolvido no backend via contacts.customerId → contactIds)
    if (filters.contactCustomerId && filters.contactCustomerId.length > 0) {
      backendFilters.contactCustomerId = filters.contactCustomerId;
    }

    // ✅ Legacy: "Meus atendimentos" toggle
    if (filters.assigned && state.user) {
      backendFilters.assigneeId = state.user.id;
    }

    // Toggle filter
    if (filters.hasUnreadMessages) {
      backendFilters.hasUnreadMessages = true;
    }

    // Date range
    if (filters.dateFrom) {
      backendFilters.createdFrom = filters.dateFrom;
    }
    if (filters.dateTo) {
      backendFilters.createdTo = filters.dateTo;
    }

    // Search
    if (searchQuery.trim()) {
      backendFilters.search = searchQuery.trim();
    }

    return backendFilters;
  }, [filters, searchQuery, state.user]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useInfiniteConversations({
    conversationType,
    limit: 25,
    additionalFilters
  });

  // ✅ Provider info is already populated in conversation.provider by backend
  // No need for complex mapping - just pass conversation.provider.type directly

  // ✅ Ref para detectar quando usuário chega ao fim da lista
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // ✅ IntersectionObserver para carregar mais ao rolar
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Quando o elemento trigger está visível, carrega próxima página
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.5, // Carrega quando 50% do elemento está visível
        rootMargin: '100px' // Inicia carregamento 100px antes
      }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ✅ Flatten de todas as páginas em um array único
  const conversations = data?.pages.flatMap((page: any) => page.items) ?? [];

  // ✅ Navegação por teclado: setas cima/baixo para alternar conversa selecionada
  const handleKeyboardNavigation = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

    // Ignorar quando foco está em input/textarea/select
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if ((e.target as HTMLElement)?.isContentEditable) return;

    if (conversations.length === 0 || !onSelectConversation) return;

    e.preventDefault();

    const currentIndex = selectedConversationId
      ? conversations.findIndex((c: any) => c.id === selectedConversationId)
      : -1;

    let nextIndex: number;
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex < conversations.length - 1 ? currentIndex + 1 : currentIndex;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    }

    if (nextIndex !== currentIndex || currentIndex === -1) {
      onSelectConversation(conversations[nextIndex].id);
    }
  }, [conversations, selectedConversationId, onSelectConversation]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardNavigation);
    return () => window.removeEventListener('keydown', handleKeyboardNavigation);
  }, [handleKeyboardNavigation]);

  // ✅ Estado de loading inicial
  if (isLoading) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-4"
        data-testid="conversation-list-loading"
      >
        <div className="flex flex-col items-center gap-2">
          <Icon
            name="loader"
            className="h-6 w-6 animate-spin"
            style={{ color: 'var(--theme-button-primary)' }}
          />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Carregando conversas...
          </p>
        </div>
      </div>
    );
  }

  // ✅ Estado de erro
  if (isError) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-4"
        data-testid="conversation-list-error"
      >
        <div className="flex flex-col items-center text-center max-w-[240px]">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-full mb-3"
            style={{
              backgroundColor: 'var(--theme-error-subtle)',
              color: 'var(--theme-error-color)'
            }}
          >
            <Icon name="alert-triangle" className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--theme-error-color)' }}>
            Erro ao carregar conversas
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
        </div>
      </div>
    );
  }

  // ✅ Estado vazio
  if (conversations.length === 0) {
    // Quando sidebar comprimida, retorna apenas container vazio sem textos
    if (!expanded) {
      return <div className="flex-1"></div>;
    }

    const hasActiveFilters = Boolean(searchQuery) || Object.keys(filters).length > 0;

    // Quando expandida, mostra o empty state universal (F51 — ícones contextuais)
    return (
      <div className="flex-1 p-3" data-testid="conversation-list-empty">
        <ListPageEmpty
          icon={
            hasActiveFilters ? 'search'
            : conversationType === 'group' ? 'users'
            : conversationType === 'ai' ? 'zap'
            : 'message-circle'
          }
          message={
            hasActiveFilters
              ? 'Nenhuma conversa encontrada com os filtros aplicados'
              : 'Nenhuma conversa encontrada'
          }
          description={
            hasActiveFilters
              ? 'Tente ajustar os filtros ou a busca'
              : conversationType === 'individual' ? 'Comece uma nova conversa individual'
              : conversationType === 'group' ? 'Nenhum grupo disponível'
              : 'Nenhuma conversa com IA'
          }
        />
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-y-auto"
      style={{ padding: '6px' }}
      data-testid="conversation-list"
    >
      {/* ✅ Lista de conversas */}
      {conversations.map((conversation) => (
        <ConversationItem
          key={conversation.id}
          conversation={conversation}
          isSelected={conversation.id === selectedConversationId}
          onClick={() => onSelectConversation?.(conversation.id, searchQuery)}
          expanded={expanded}
          providerId={conversation.provider?.type}
        />
      ))}

      {/* ✅ Trigger point para carregar mais */}
      {hasNextPage && (
        <div
          ref={loadMoreRef}
          className="py-3 flex items-center justify-center"
          data-testid="conversation-list-load-more"
        >
          {isFetchingNextPage ? (
            <div
              className="flex items-center gap-2"
              style={{ color: 'var(--theme-button-primary)' }}
            >
              <Icon name="loader" className="h-4 w-4 animate-spin" />
              <span className="text-xs font-medium">Carregando mais...</span>
            </div>
          ) : (
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              Role para carregar mais
            </span>
          )}
        </div>
      )}

      {/* ✅ Indicador de fim da lista - Apenas quando sidebar expandida */}
      {expanded && !hasNextPage && conversations.length > 0 && (
        <div className="py-3 text-center" data-testid="conversation-list-end">
          <span
            className="text-xs tabular-nums"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Todas as conversas carregadas ({conversations.length})
          </span>
        </div>
      )}
    </div>
  );
};
